import React from "react";
import rcsLogo from "../../assets/RCS-Logo-Blue.png";

const CheckVoucherPreview = ({
  voucherNo,
  voucherDate,
  payee,
  checkNo,
  checkDate,
  details = [],
  amountLabel = "Amount",
  amount,
  amountWords,
  preparerName,
  assignedCheckerName,
  checkedByName,
  approvedByName,
  receivedByName,
  preparerSignatureUrl,
  checkedBySignatureUrl,
  lines = [],
  requests = [],
  forSoa = false,
  soaRemarks = "",
  soaFileName = "",
  soaFileUrl = "",
}) => {
  const formatMoney = (value) =>
    Number(value || 0).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const getAmountCurrencyMeta = (currencyValue) => {
    const code = String(currencyValue || "PHP").toUpperCase();
    return {
      code,
      symbol: code === "USD" ? "$" : "₱",
      locale: code === "USD" ? "en-US" : "en-PH",
    };
  };

  const getUsdConvertedAmount = (phpAmount, exchangeRate) => {
    const rate = Number(exchangeRate || 1);
    return rate > 0 ? Number(phpAmount || 0) / rate : Number(phpAmount || 0);
  };

  const getEffectiveEwtAmount = (particular, netAmount) => {
    const storedAmount = Number(particular?.ewt_amount || 0);
    if (storedAmount > 0) return storedAmount;
    const rate = Number(particular?.ewt_rate || 0);
    if (!particular?.ewt_type || rate <= 0) return 0;
    return Number(((Number(netAmount || 0) * rate) / 100).toFixed(2));
  };

  const getShipmentConsignee = (request) =>
    request?.shipment_consignee || request?.receiver_name || "";

  const getEffectiveTaxRate = (particular) => {
    const numericRate = Number(particular?.tax_rate || 0);
    if (numericRate > 0) return numericRate;
    return particular?.with_tax ? 12 : 0;
  };

  const getWithTaxBreakdown = (total, taxRate = 12) => {
    const grossTotal = Number(total || 0);
    const rate = Number(taxRate || 0);
    if (!rate) {
      return {
        netAmount: grossTotal,
        inputTaxAmount: 0,
      };
    }
    const netAmount = Number((grossTotal / (1 + rate / 100)).toFixed(2));
    const inputTaxAmount = Number((netAmount * (rate / 100)).toFixed(2));

    return {
      netAmount,
      inputTaxAmount,
    };
  };

  const getEntryDisplay = (line) => {
    const role = String(line?.role || "").trim();
    if (role === "account_title") {
      return {
        account: "—",
        particulars: "—",
      };
    }
    if (role === "input_tax" || role === "particulars_tax") {
      return {
        account: line.account_name || "—",
        particulars: line.particulars || "—",
      };
    }
    if (role === "ewt" || role === "particulars_ewt") {
      return {
        account: line.account_name || "—",
        particulars: line.particulars || "—",
      };
    }
    if (role === "cash") {
      return {
        account: "—",
        particulars: line.particulars || "Check payment",
      };
    }
    return {
      account: line.account_name || "—",
      particulars: line.particulars || "—",
    };
  };

  const lineRows = lines.map((line, index) => {
    const display = getEntryDisplay(line);
    return (
      <tr key={line.id || index}>
        <td className="border border-gray-800 px-3 py-2">{display.account}</td>
        <td className="border border-gray-800 px-3 py-2">{display.particulars}</td>
        <td className="border border-gray-800 px-3 py-2 text-right">
          {line.debit ? `₱${formatMoney(line.debit)}` : ""}
        </td>
        <td className="border border-gray-800 px-3 py-2 text-right">
          {line.credit ? `₱${formatMoney(line.credit)}` : ""}
        </td>
      </tr>
    );
  });

  const entryTotals = lines.reduce(
    (totals, line) => ({
      debit: totals.debit + Number(line.debit || 0),
      credit: totals.credit + Number(line.credit || 0),
    }),
    { debit: 0, credit: 0 }
  );

  return (
    <div
      className="overflow-hidden rounded-2xl border border-gray-200 bg-white p-5 text-[13px] leading-relaxed"
      style={{ fontFamily: '"Times New Roman", serif' }}
    >
      <div className="flex flex-col items-center justify-center gap-1">
        <img src={rcsLogo} alt="RCS Logistics Philippines" className="max-h-16 w-auto object-contain" />
        <div className="text-2xl font-bold tracking-[0.22em] text-gray-900">CHECK VOUCHER</div>
        <div className="text-sm font-semibold tracking-[0.12em] text-gray-600">{voucherNo || "—"}</div>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2">
        <div><strong>Date:</strong> {voucherDate || "—"}</div>
        <div><strong>Payee:</strong> {payee || "—"}</div>
        <div><strong>Check No.:</strong> {checkNo || "—"}</div>
        <div><strong>Check Date:</strong> {checkDate || "—"}</div>
        {forSoa && <div><strong>For SOA:</strong> Yes</div>}
        {forSoa && <div><strong>SOA Remarks:</strong> {soaRemarks || "—"}</div>}
        {details.map((item, index) => (
          <div key={`${item.label || "detail"}-${index}`}>
            <strong>{item.label}:</strong> {item.value || "—"}
          </div>
        ))}
        <div><strong>Prepared by:</strong> {preparerName || "—"}</div>
        <div className="sm:col-span-2">
          <strong>{amountLabel}:</strong> ₱{formatMoney(amount)}
        </div>
        <div className="sm:col-span-2">
          <strong>Amount in Words:</strong> {amountWords || "—"}
        </div>
      </div>

      <div className="mt-5">
        <div className="mb-2 text-xs font-semibold uppercase tracking-[0.14em] text-gray-500">Accounting Entries</div>
        <div className="overflow-hidden border border-gray-800">
          <table className="w-full border-collapse">
            <thead className="bg-gray-50">
              <tr>
                <th className="border border-gray-800 px-3 py-2 text-left">Account</th>
                <th className="border border-gray-800 px-3 py-2 text-left">Particulars</th>
                <th className="border border-gray-800 px-3 py-2 text-right">Debit</th>
                <th className="border border-gray-800 px-3 py-2 text-right">Credit</th>
              </tr>
            </thead>
            <tbody>
              {lineRows}
              <tr className="font-semibold">
                <td className="border border-gray-800 px-3 py-2 text-right">TOTAL</td>
                <td className="border border-gray-800 px-3 py-2"></td>
                <td className="border border-gray-800 px-3 py-2 text-right">₱{formatMoney(entryTotals.debit)}</td>
                <td className="border border-gray-800 px-3 py-2 text-right">₱{formatMoney(entryTotals.credit)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-4 text-sm space-y-1">
        <div><strong>Assigned checker:</strong> {assignedCheckerName || "Pending"}</div>
        <div><strong>Checked by:</strong> {checkedByName || "Pending"}</div>
        <div><strong>Approved by:</strong> {approvedByName || "EDR/ERI/AGR"}</div>
        <div><strong>Received by:</strong> {receivedByName || "Pending"}</div>
      </div>

      <div className="grid grid-cols-4 gap-3 mt-4">
        <div className="text-center">
          <div className="h-14 flex items-end justify-center">
            {preparerSignatureUrl ? (
              <img src={preparerSignatureUrl} alt="Preparer signature" className="max-h-12 object-contain" />
            ) : null}
          </div>
          <div className="border-b border-gray-800 mb-1" />
          <div className="text-[12px] leading-tight font-medium">{preparerName || "Name"}</div>
          <div>Prepared by</div>
        </div>
        <div className="text-center">
          <div className="h-14 flex items-end justify-center">
            {checkedBySignatureUrl ? (
              <img src={checkedBySignatureUrl} alt="Checked by signature" className="max-h-12 object-contain" />
            ) : null}
          </div>
          <div className="border-b border-gray-800 mb-1" />
          <div className="text-[12px] leading-tight font-medium">{checkedByName || "Name"}</div>
          <div>Checked by</div>
        </div>
        <div className="text-center">
          <div className="h-14 flex items-end justify-center">
            <div className="h-12" />
          </div>
          <div className="border-b border-gray-800 mb-1" />
          <div className="text-[12px] leading-tight font-medium">{approvedByName || "Name"}</div>
          <div>Approved by</div>
        </div>
        <div className="text-center">
          <div className="h-14 flex items-end justify-center">
            <div className="h-12" />
          </div>
          <div className="border-b border-gray-800 mb-1" />
          <div className="text-[12px] leading-tight font-medium">{receivedByName || "Name"}</div>
          <div>Received by</div>
        </div>
      </div>

      {requests && requests.length > 0 && (
        <div className="mt-6 pt-6 border-t-2 border-gray-300">
          <h2 className="text-lg font-bold text-center mb-4 tracking-wider">REQUEST DETAILS</h2>
          {requests.map((request, index) => {
            const particulars = Array.isArray(request.particulars) ? request.particulars : [];
            const itemCurrency = String(request.currency || "PHP").toUpperCase();
            const itemExchangeRate = Number(request.exchange_rate || 1);
            const isUsdItem = itemCurrency === "USD" && itemExchangeRate > 0;
            const currencySymbol = isUsdItem ? "$" : "₱";
            const requestTotalPhp = Number(request.total_amount || 0);
            const requestTotalOriginal = isUsdItem ? requestTotalPhp / itemExchangeRate : requestTotalPhp;
            const requestTotalEwtPhp = particulars.reduce((sum, particular) => {
              const totalOriginal = Number(
                particular.total || (particular.quantity || 1) * (particular.amount || 0)
              );
              const totalPhp = isUsdItem ? totalOriginal * itemExchangeRate : totalOriginal;
              const effectiveTaxRate = getEffectiveTaxRate(particular);
              const { netAmount } = particular.with_tax
                ? getWithTaxBreakdown(totalPhp, effectiveTaxRate)
                : { netAmount: totalPhp };
              return sum + getEffectiveEwtAmount(particular, particular.with_tax ? netAmount : totalPhp);
            }, 0);
            const requestTotalEwtOriginal = isUsdItem ? requestTotalEwtPhp / itemExchangeRate : requestTotalEwtPhp;
            const requestNetPayablePhp = Number((requestTotalPhp - requestTotalEwtPhp).toFixed(2));
            const requestNetPayableOriginal = isUsdItem
              ? requestNetPayablePhp / itemExchangeRate
              : requestNetPayablePhp;
            return (
              <div key={request.id || index} className="mb-8 pb-6 border-b border-gray-200 last:border-b-0">
                {/* Header */}
                <h3 className="text-xl font-bold text-center tracking-widest mb-1">CASH VOUCHER</h3>
                <p className="text-center font-semibold mb-4 text-sm">RFP No: {request.rfp_no || "—"}</p>

                {/* Info Section */}
                <div className="mb-4 text-[13px] grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div><strong>Date:</strong> {request.created_at ? new Date(request.created_at).toLocaleDateString() : "—"}</div>
                  <div><strong>Payee:</strong> {request.payee || "—"}</div>
                  <div><strong>Job Order:</strong> {request.job_order || "—"}</div>
                  {request.job_order && getShipmentConsignee(request) ? (
                    <div><strong>Shipment Consignee:</strong> {getShipmentConsignee(request)}</div>
                  ) : null}
                  <div><strong>Due Date:</strong> {request.due_date ? new Date(request.due_date).toLocaleDateString() : "—"}</div>
                  <div><strong>Currency:</strong> {itemCurrency}</div>
                  <div><strong>Mode of Payment:</strong> {request.mode_of_payment || "—"}</div>
                  <div><strong>Corporate Client:</strong> {request.corporate_client || "—"}</div>
                  {forSoa && <div><strong>For SOA:</strong> Yes</div>}
                  {forSoa && <div><strong>SOA Remarks:</strong> {soaRemarks || "—"}</div>}
                  <div className="sm:col-span-2"><strong>Shipment Description:</strong> {request.shipment_description || "—"}</div>
                </div>

                {/* Attachments */}
                {request.attachments && request.attachments.length > 0 ? (
                  <div className="mb-4 rounded-2xl border border-gray-200 bg-gray-50 p-4">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="font-semibold text-gray-800">Attached documents</p>
                        <p className="text-xs text-gray-500">
                          Files uploaded with this request. Review them before approving.
                        </p>
                      </div>
                      <span className="text-xs font-semibold uppercase tracking-[0.12em] text-gray-500">
                        {request.attachments.length} file(s)
                      </span>
                    </div>
                    <div className="mt-3 space-y-2">
                      {request.attachments.map((doc, docIndex) => (
                        <a
                          key={docIndex}
                          href={doc.file_url || "#"}
                          target="_blank"
                          rel="noreferrer"
                          className="flex items-center justify-between gap-3 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm hover:bg-gray-50"
                        >
                          <div className="min-w-0">
                            <p className="truncate font-medium text-gray-800">{doc.file_name || "Attachment"}</p>
                            <p className="text-xs text-gray-500">
                              {(doc.file_type || "file").toString()} •{" "}
                              {doc.created_at ? new Date(doc.created_at).toLocaleString() : "Uploaded"}
                            </p>
                          </div>
                          <span className="text-xs font-semibold text-blue">Open</span>
                        </a>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="mb-4 rounded-2xl border border-dashed border-gray-200 bg-gray-50 p-4">
                    <p className="font-semibold text-gray-800">Attached documents</p>
                    <p className="mt-1 text-sm text-gray-600">
                      No attached documents were uploaded for this request.
                    </p>
                  </div>
                )}

                {/* Particulars Table */}
                <table className="w-full border-collapse text-[13px] mb-4">
                  <thead>
                    <tr>
                      <th className="border border-gray-800 px-3 py-2 text-left w-[40%]">
                        PARTICULARS
                        <br />
                        <small className="font-normal text-gray-600">Reference / Description / Quantity / Unit cost</small>
                      </th>
                      <th className="border border-gray-800 px-3 py-2 text-right w-[12%]">NET AMOUNT</th>
                      <th className="border border-gray-800 px-3 py-2 text-right w-[12%]">TAX</th>
                      <th className="border border-gray-800 px-3 py-2 text-right w-[12%]">EWT</th>
                      <th className="border border-gray-800 px-3 py-2 text-right w-[14%]">TOTAL</th>
                    </tr>
                  </thead>
                  <tbody>
                    {particulars.map((particular, pIndex) => {
                      const totalOriginal = Number(
                        particular.total || (particular.quantity || 1) * (particular.amount || 0)
                      );
                      const totalPhp = isUsdItem ? totalOriginal * itemExchangeRate : totalOriginal;
                      const effectiveTaxRate = getEffectiveTaxRate(particular);
                      const { netAmount, inputTaxAmount } = particular.with_tax
                        ? getWithTaxBreakdown(totalPhp, effectiveTaxRate)
                        : { netAmount: 0, inputTaxAmount: 0 };
                      const netAmountUsd = isUsdItem && itemExchangeRate > 0 ? getUsdConvertedAmount(netAmount, itemExchangeRate) : 0;
                      const inputTaxAmountUsd = isUsdItem && itemExchangeRate > 0 ? getUsdConvertedAmount(inputTaxAmount, itemExchangeRate) : 0;
                      const ewtAmountPhp = getEffectiveEwtAmount(particular, particular.with_tax ? netAmount : totalPhp);
                      const ewtAmountUsd = isUsdItem && itemExchangeRate > 0 ? getUsdConvertedAmount(ewtAmountPhp, itemExchangeRate) : 0;
                      return (
                        <tr key={pIndex}>
                          <td className="border border-gray-800 px-3 py-2">
                            <div>{particular.description || particular.name || "—"}</div>
                            <div className="text-gray-600">
                              <strong>Qty:</strong> {particular.quantity || particular.qty || 1} &nbsp;
                              <strong>Unit Cost:</strong> {currencySymbol}
                              {formatMoney(particular.amount || 0)}
                            </div>
                            {isUsdItem && (
                              <div className="text-gray-600">
                                <strong>Currency:</strong> {itemCurrency} &nbsp;
                                <strong>Conversion Rate:</strong> {itemExchangeRate.toFixed(4)}
                              </div>
                            )}
                          </td>
                          <td className="border border-gray-800 px-3 py-2 text-right">
                            {particular.with_tax ? (
                              isUsdItem ? (
                                <div className="flex flex-col">
                                  <span>{currencySymbol}{formatMoney(netAmountUsd)}</span>
                                  <span className="text-xs text-gray-500">₱{formatMoney(netAmount)}</span>
                                </div>
                              ) : `₱${formatMoney(netAmount)}`
                            ) : "—"}
                          </td>
                          <td className="border border-gray-800 px-3 py-2 text-right">
                            {particular.with_tax ? (
                              isUsdItem ? (
                                <div className="flex flex-col">
                                  <span>{currencySymbol}{formatMoney(inputTaxAmountUsd)}</span>
                                  <span className="text-xs text-gray-500">₱{formatMoney(inputTaxAmount)}</span>
                                </div>
                              ) : `₱${formatMoney(inputTaxAmount)}`
                            ) : "—"}
                          </td>
                          <td className="border border-gray-800 px-3 py-2 text-right">
                            {ewtAmountPhp > 0 ? (
                              isUsdItem ? (
                                <div className="flex flex-col">
                                  <span>{currencySymbol}{formatMoney(ewtAmountUsd)}</span>
                                  <span className="text-xs text-gray-500">₱{formatMoney(ewtAmountPhp)}</span>
                                </div>
                              ) : `₱${formatMoney(ewtAmountPhp)}`
                            ) : "—"}
                          </td>
                          <td className="border border-gray-800 px-3 py-2 text-right font-semibold">
                            {isUsdItem ? (
                              <div className="flex flex-col">
                                <span>{currencySymbol}{formatMoney(totalOriginal)}</span>
                                <span className="text-xs text-gray-500">₱{formatMoney(totalPhp)}</span>
                              </div>
                            ) : (
                              `₱${formatMoney(totalPhp)}`
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    {/* Empty row for spacing */}
                    <tr>
                      <td className="border border-gray-800 h-16"></td>
                      <td className="border border-gray-800"></td>
                      <td className="border border-gray-800"></td>
                      <td className="border border-gray-800"></td>
                      <td className="border border-gray-800"></td>
                    </tr>
                  </tbody>
                </table>

                {/* Total */}
                <div className="flex justify-end mb-4">
                  <div className="flex flex-col gap-2 items-end">
                    <div className="border border-gray-800 px-4 py-2 min-w-[220px]">
                      <strong>TOTAL {getAmountCurrencyMeta(request.currency).code}</strong>{" "}
                      {isUsdItem ? (
                        <div className="inline-flex flex-col items-end">
                          <span>
                            {getAmountCurrencyMeta(request.currency).symbol}
                            {formatMoney(requestTotalOriginal)}
                          </span>
                          <span className="text-xs text-gray-500">
                            ₱{formatMoney(requestTotalPhp)}
                          </span>
                        </div>
                      ) : (
                        `₱${formatMoney(requestTotalPhp)}`
                      )}
                    </div>
                    {requestTotalEwtPhp > 0 && (
                      <>
                        <div className="border border-gray-800 px-4 py-2 min-w-[220px]">
                          <strong>LESS: EWT</strong>{" "}
                          {isUsdItem ? (
                            <div className="inline-flex flex-col items-end">
                              <span>
                                ({getAmountCurrencyMeta(request.currency).symbol}
                                {formatMoney(requestTotalEwtOriginal)})
                              </span>
                              <span className="text-xs text-gray-500">
                                (₱{formatMoney(requestTotalEwtPhp)})
                              </span>
                            </div>
                          ) : (
                            `(₱${formatMoney(requestTotalEwtPhp)})`
                          )}
                        </div>
                        <div className="border border-gray-800 px-4 py-2 min-w-[220px]">
                          <strong>TOTAL AMOUNT DUE</strong>{" "}
                          {isUsdItem ? (
                            <div className="inline-flex flex-col items-end">
                              <span>
                                {getAmountCurrencyMeta(request.currency).symbol}
                                {formatMoney(requestNetPayableOriginal)}
                              </span>
                              <span className="text-xs text-gray-500">
                                ₱{formatMoney(requestNetPayablePhp)}
                              </span>
                            </div>
                          ) : (
                            `₱${formatMoney(requestNetPayablePhp)}`
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CheckVoucherPreview;
